import { FC } from 'react';
import {
  Avatar,
  Flex,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';

import { UserDetails } from '../../components/user-details/user-details';
import {
  Role,
  TreatyDetailsFragment,
  TreatyUserFragment,
  UserType,
} from '../../graphql/generated';
import { convertCedantsToCedantNames } from '../../utils/treaty-utils/convert-cedants-to-cedant-names';
import { LastLogin } from './components/last-login';
import { UserBlockButton } from './components/user-block-button/user-block-button';
import { UserUnblockButton } from './components/user-unblock-button/user-unblock-button';

type UsersTableProps = {
  users: TreatyUserFragment[];
  roles: Role[];
  treaties: TreatyDetailsFragment[];
  startTimedPolling: () => void;
  selectedRoles?: string[];
};

export const UsersTable: FC<UsersTableProps> = ({
  users,
  roles,
  treaties,
  startTimedPolling,
  selectedRoles = [],
}) => {
  return (
    <TableContainer>
      <Table variant="unstyled">
        <Thead>
          <Tr color="gray.600" borderBottom="1px solid" borderColor="gray.200">
            <Th></Th>
            <Th>Name</Th>
            <Th>Roles</Th>
            <Th>Email Notifications</Th>
            <Th>Last Login</Th>
            <Th textAlign="right">Logins</Th>
            <Th></Th>
          </Tr>
        </Thead>

        <Tbody>
          {users?.map((user) => {
            const emailNotifications = convertCedantsToCedantNames(
              user.emailNotifications,
              treaties
            );

            const canBlock =
              user.canBlock && user.type === UserType.EXTERNAL_USER;

            return (
              <Tr
                key={user.id}
                data-testid="user-row"
                borderBottom="1px solid"
                borderColor="gray.100"
                verticalAlign="baseline"
                _last={{ borderBottom: 'none' }}
              >
                <Td p={0}>
                  <Avatar
                    name={user.name}
                    bg={user.isBlocked ? 'gray.300' : 'blue.100'}
                    color={user.isBlocked ? 'gray.50' : 'blue.400'}
                  />
                </Td>
                <Td>
                  <UserDetails user={user} roles={roles} treaties={treaties} />
                </Td>
                <Td minWidth="200px">
                  <Flex flexWrap="wrap">
                    {user.roles?.map((role) => (
                      <Tag
                        as="div"
                        key={role}
                        mr={2}
                        my={1}
                        backgroundColor={
                          selectedRoles.includes(role)
                            ? 'yellow.200'
                            : undefined
                        }
                      >
                        {role}
                      </Tag>
                    ))}
                  </Flex>
                </Td>
                <Td>
                  <Flex flexWrap="wrap">
                    {emailNotifications?.map((email) => (
                      <Tag as="div" key={email} mr={2} my={1}>
                        {email}
                      </Tag>
                    ))}
                  </Flex>
                </Td>
                <Td>
                  <LastLogin lastLogin={user.lastLogin} />
                </Td>
                <Td textAlign="right">{user.loginCount}</Td>
                <Td textAlign="right">
                  {canBlock &&
                    (user.isBlocked ? (
                      <UserUnblockButton
                        userId={user.id}
                        userName={user.name}
                        startTimedPolling={startTimedPolling}
                      />
                    ) : (
                      <UserBlockButton
                        userId={user.id}
                        userName={user.name}
                        startTimedPolling={startTimedPolling}
                      />
                    ))}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
